import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, Edit, Trash, Check, X, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { adminApi } from '../utils';

// Users Management Panel Component
const UsersManagementPanel = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [adjustingPoints, setAdjustingPoints] = useState(null);
  const [pointsAdjustment, setPointsAdjustment] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [resettingPassword, setResettingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const response = await adminApi.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAdjustPoints = async (userId) => {
    const points = parseInt(pointsAdjustment);
    if (isNaN(points) || points === 0) {
      toast.error('Please enter a valid point adjustment');
      return;
    }

    try {
      const response = await adminApi.post(
        `/admin/users/${userId}/adjust-points?points=${points}&reason=${encodeURIComponent(adjustmentReason)}`
      );
      
      if (response.data.success) {
        toast.success(`Points adjusted! New balance: ${response.data.new_balance}`);
        setAdjustingPoints(null);
        setPointsAdjustment('');
        setAdjustmentReason('');
        fetchUsers();
      }
    } catch (error) {
      console.error('Error adjusting points:', error);
      toast.error('Failed to adjust points');
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user "${userEmail}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await adminApi.delete(`/admin/users/${userId}`);
      
      if (response.data.success) {
        toast.success(`User deleted: ${userEmail}`);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      const response = await adminApi.post(
        `/admin/users/${userId}/reset-password?new_password=${encodeURIComponent(newPassword)}`
      );
      if (response.data.success) {
        toast.success('Password reset!');
        setResettingPassword(null);
        setNewPassword('');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredUsers.length} registered users
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <Card className="p-8 text-center">
          <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            {searchTerm ? 'No users found matching your search.' : 'No registered users yet.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map((user) => (
            <Card key={user.id} className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold truncate">{user.name || 'No name'}</h4>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {user.order_count} orders
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Total spent: ${user.total_spent?.toFixed(2) || '0.00'} • Joined: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="text-center px-4 py-2 bg-amber-50 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium">Plant Perks</p>
                    <p className="text-xl font-bold text-amber-700">{user.loyalty_points}</p>
                  </div>
                  
                  {adjustingPoints === user.id ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2 items-center">
                        <select
                          value={pointsAdjustment.startsWith('-') ? 'remove' : 'add'}
                          onChange={(e) => {
                            const currentVal = Math.abs(parseInt(pointsAdjustment) || 0);
                            setPointsAdjustment(e.target.value === 'remove' ? `-${currentVal}` : String(currentVal));
                          }}
                          className="h-8 px-2 text-sm border rounded bg-white"
                        >
                          <option value="add">Add</option>
                          <option value="remove">Remove</option>
                        </select>
                        <Input
                          type="number"
                          inputMode="numeric"
                          placeholder="0"
                          min="0"
                          value={Math.abs(parseInt(pointsAdjustment) || 0) || ''}
                          onChange={(e) => {
                            const val = Math.abs(parseInt(e.target.value) || 0);
                            const isRemove = pointsAdjustment.startsWith('-');
                            setPointsAdjustment(isRemove ? `-${val}` : String(val));
                          }}
                          className="w-20 h-8 text-sm text-center"
                        />
                        <span className="text-xs text-muted-foreground">pts</span>
                      </div>
                      <Input
                        placeholder="Reason (optional)"
                        value={adjustmentReason}
                        onChange={(e) => setAdjustmentReason(e.target.value)}
                        className="w-full h-8 text-sm"
                      />
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleAdjustPoints(user.id)}
                          className="h-7"
                        >
                          <Check className="w-3 h-3 mr-1" /> Save
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => {
                            setAdjustingPoints(null);
                            setPointsAdjustment('');
                            setAdjustmentReason('');
                          }}
                          className="h-7"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : resettingPassword === user.id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="password"
                        placeholder="New password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-32 h-8 text-sm"
                      />
                      <Button size="sm" onClick={() => handleResetPassword(user.id)} className="h-8">
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setResettingPassword(null); setNewPassword(''); }} className="h-8">
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setAdjustingPoints(user.id)}
                        className="whitespace-nowrap"
                      >
                        <Edit className="w-3 h-3 mr-1" /> Perks
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => setResettingPassword(user.id)}
                        className="whitespace-nowrap"
                      >
                        🔑
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleDeleteUser(user.id, user.email)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default UsersManagementPanel;
